<template>
  <div
    class="high-risk-report-launcher"
    :data-status-settled="String(isStatusSettled)"
  >
    <HighRiskReportDialog
      v-if="newResponse && riskAssessmentId"
      :new-response="newResponse"
      :risk-assessment-id="riskAssessmentId"
      @close="closeDialog"
      @reported="handleReported"
    />
    <HighRiskReportThanksDialog
      v-if="isThanksOpen"
      @close="closeThanks"
    />
    <v-snackbar
      v-model="snackbar.open"
      color="error"
      location="bottom"
      :timeout="SNACKBAR_TIMEOUT"
      role="status"
    >
      {{ snackbar.message }}
      <template #actions>
        <v-btn
          variant="text"
          @click="snackbar.open = false"
        >
          閉じる
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import {
  canUseHighRiskReport,
  highRiskReportsApi,
  type HighRiskReportAvailableResponse,
  type HighRiskReportConsultableType,
  type HighRiskReportNewResponse,
  type HighRiskReportResult
} from "@/vue/entities/high-risk-report";
import {
  deniedReasonMessage,
  HighRiskReportDialog,
  HighRiskReportThanksDialog,
  resolveErrorMessage
} from "@/vue/features/high-risk-report";
import { FEATURE_FLAGS } from "@/vue/shared/utils/featureFlags";

const SNACKBAR_TIMEOUT = 4000;

const props = defineProps<{
  consultableType: HighRiskReportConsultableType;
  consultableId: string;
}>();

const store = useStore();
// フラグB（#2599）有効時はボトムバーの導線ごと無効にし、表示判定のための API も呼ばない
const isPermitted = computed<boolean>(() => canUseHighRiskReport(store?.getters?.capabilities)
  && store?.getters?.isFeatureEnabled?.(FEATURE_FLAGS.hideHighRiskBarActions) !== true);

const emit = defineEmits<{
  reported: [HighRiskReportResult];
  "status-changed": [{ reportable: boolean; riskAssessmentAvailable: boolean; riskAssessmentRequired: boolean }];
  "settled-changed": [boolean];
}>();

const newResponse = ref<HighRiskReportAvailableResponse | null>(null);
const isThanksOpen = ref(false);
const riskAssessmentId = ref<string | null>(null);
const isLoading = ref(false);
// ボトムバーのボタン構成は、権限の判定と報告可否の取得結果を受けて後から変わる。
// 位置の安定だけでは取得の完了を保証できないため、system spec が確定を待てるよう DOM（data-status-settled）に公開する。
const hasSettledStatus = ref(false);
// 重複した取得のうち、最後に開始したものの完了だけを確定として扱うための番号。
let latestStatusRequest = 0;
const isStatusSettled = computed<boolean>(() => {
  // currentDoctor の取得前は権限が未確定で、この後に取得が始まりうる。
  if (store?.getters?.currentDoctor == null) return false;
  // 権限が無ければ取得せず、ボタン構成もこれ以上変わらない。
  if (!isPermitted.value) return true;
  return hasSettledStatus.value;
});
// 呼び出し側がボトムバーの表示を確定まで保留できるよう、確定状態を通知する。
watch(isStatusSettled, (settled) => emit("settled-changed", settled), { immediate: true });
onBeforeUnmount(() => emit("settled-changed", false));
// 完了メッセージはサンクスダイアログに移したため、スナックバーはエラー通知専用。
const snackbar = reactive({ open: false, message: "" });

const showSnackbar = (message: string): void => {
  snackbar.message = message;
  snackbar.open = true;
};

const closeDialog = (): void => {
  newResponse.value = null;
  riskAssessmentId.value = null;
};

const publishStatus = (response: HighRiskReportNewResponse, showAssessmentNotice = false): void => {
  emit("status-changed", {
    reportable: response.reportable,
    riskAssessmentAvailable: response.reportable && response.riskAssessment !== null,
    riskAssessmentRequired: showAssessmentNotice && response.reportable && response.riskAssessment === null
  });
};

const loadStatus = async (): Promise<void> => {
  if (!isPermitted.value) return;

  const consultableType = props.consultableType;
  const consultableId = props.consultableId;
  const statusRequest = ++latestStatusRequest;
  hasSettledStatus.value = false;

  try {
    const response = await highRiskReportsApi.fetchNew({
      consultableType,
      consultableId
    });
    if (props.consultableType !== consultableType || props.consultableId !== consultableId) return;

    publishStatus(response);
  } catch {
    // ボトムバーの表示判定に失敗しても、画面全体は利用可能にする。
  } finally {
    if (statusRequest === latestStatusRequest) hasSettledStatus.value = true;
  }
};

watch(isPermitted, (permitted) => {
  if (permitted) loadStatus();
}, { immediate: true });

watch(() => [props.consultableType, props.consultableId], loadStatus);

// 同一ルートで相談カルテだけが差し替わるケースへの保険。別カルテへ報告内容を引き継がせない。
watch(() => [props.consultableType, props.consultableId], closeDialog);

// 報告の根拠は、直前に入力したアセスメント（呼び出し側が渡す）を優先し、
// 無ければサーバーが返す当該カルテの直近完了分を使う（FR-002）。
const resolveRiskAssessment = (response: HighRiskReportAvailableResponse, requestedId?: string | null): string | null =>
  requestedId || response.riskAssessment?.id || null;

/**
 * 報告フローを開始する。ボトムバー / リスクアセスメント完了画面から呼び出される（別チケット）。
 * riskAssessmentId を渡さない場合は、当該カルテの直近の完了済みアセスメントを根拠にする。
 */
const open = async ({ riskAssessmentId: requestedId }: { riskAssessmentId?: string | null } = {}): Promise<void> => {
  if (isLoading.value) return;

  isLoading.value = true;
  const consultableType = props.consultableType;
  const consultableId = props.consultableId;
  try {
    const response = await highRiskReportsApi.fetchNew({
      consultableType,
      consultableId
    });
    if (props.consultableType !== consultableType || props.consultableId !== consultableId) return;

    publishStatus(response, true);

    // 担当外・期間超過は 200 + reportable: false で返るため、開かずにメッセージのみ表示する。
    if (!response.reportable) {
      showSnackbar(deniedReasonMessage(response.deniedReason));
      return;
    }

    const resolvedId = resolveRiskAssessment(response, requestedId);
    if (!resolvedId) return;

    newResponse.value = response;
    riskAssessmentId.value = resolvedId;
  } catch (error) {
    showSnackbar(resolveErrorMessage(error));
  } finally {
    isLoading.value = false;
  }
};

const handleReported = (result: HighRiskReportResult): void => {
  closeDialog();
  isThanksOpen.value = true;
  emit("reported", result);
};

const closeThanks = (): void => {
  isThanksOpen.value = false;
};

defineExpose({ open, isLoading });
</script>
